import React, { useState, useMemo, useEffect } from "react";
import {
  startOfMonth,
  endOfQuarter,
  startOfYear,
  endOfYear,
  format,
  isValid,
  compareAsc,
  parseISO,
  startOfQuarter,
} from "date-fns";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { camelCase, startCase } from "lodash";
import {
  Box,
  VStack,
  HStack,
  ButtonPrimary,
  ButtonPrimary1,
} from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import SearchInput from "../../../components/shared/SearchInput";
import { CollapsibleComponent } from "../../../components/shared/Collapsible";
import { formatDisplayDate } from "../../../utils/date";
import { BlackScholesV2, BlackScholes } from "../../../types/blackScholes";
import {
  downloadOtherAssumptionsTemplate,
  uploadOtherAssumptionsTemplate,
} from "../../../api/black-scholes";

interface OtherAssumptionsProps {
  grantScopeData: BlackScholesV2 | undefined;
  onBack: () => void;
  onNext: (data: any) => void;
  setGrantScopeData?: (data: any) => void;
}

interface GroupData {
  valuationDateStr: string;
  valuationDate: Date;
  grantDates: string[];
  grantCount: number;
}

export const OtherAssumptions: React.FC<OtherAssumptionsProps> = ({
  grantScopeData,
  onBack,
  onNext,
  setGrantScopeData,
}) => {
  const [groupAssumptions, setGroupAssumptions] = useState<{
    [key: string]: {
      volatility: number;
      riskFreeRate: number;
      stockPrice: number;
      dividendYield: number;
    };
  }>({});

  const [collapsedAccordions, setCollapsedAccordions] = useState<{
    [key: string]: boolean;
  }>({});

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const handleDownloadTemplate = async () => {
    try {
      const valuationDates: string[] = [];
      Object.keys(groupAssumptions).forEach((valDateStr) => {
        valuationDates.push(format(new Date(valDateStr), "yyyy-MM-dd"));
      });
      const response = await downloadOtherAssumptionsTemplate({
        valuationDates,
      });
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "other_assumptions_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      toast.error("Error downloading template");
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    if (fileExtension !== "xlsx") {
      toast.error("Please upload a valid Excel file (.xlsx)");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);

    try {
      const valuationDates: string[] = [];

      Object.keys(groupAssumptions).forEach((valDateStr) => {
        valuationDates.push(format(new Date(valDateStr), "yyyy-MM-dd"));
      });

      const result = await uploadOtherAssumptionsTemplate(file, valuationDates);

      if (result.errors && result.errors.length > 0) {
        setUploadErrors(result.errors);
      } else if (result.assumptions) {
        setGroupAssumptions((prev) => {
          const next = { ...prev };
          Object.keys(result.assumptions).forEach((valDateStr) => {
            const formattedDate = format(new Date(valDateStr), "yyyy-MM-dd");
            if (
              next[formattedDate] ||
              groups.find((g) => g.valuationDateStr === formattedDate)
            ) {
              next[formattedDate] = {
                ...next[formattedDate],
                ...result.assumptions[valDateStr],
              };
            }
          });
          return next;
        });
        toast.success("Assumptions updated successfully");
      }
    } catch (error) {
      toast.error("Failed to upload assumptions");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // 1. Group grants by Valuation Date
  const groups = useMemo(() => {
    if (!grantScopeData?.grantDatesWithData) return [];

    const grouped: { [key: string]: GroupData } = {};

    Object.entries(grantScopeData.grantDatesWithData).forEach(
      ([grantDate, data]) => {
        if (grantDate === "groupByGrantDateAndStrikePrice") return;
        if (typeof data !== "object" || !data || !("valuationDate" in data))
          return;

        const valDate = new Date(data.valuationDate!);
        if (!isValid(valDate)) return;

        const valDateStr = format(valDate, "yyyy-MM-dd");

        if (!grouped[valDateStr]) {
          grouped[valDateStr] = {
            valuationDateStr: valDateStr,
            valuationDate: valDate,
            grantDates: [],
            grantCount: 0,
          };
        }

        grouped[valDateStr].grantDates.push(grantDate);
        grouped[valDateStr].grantCount++;
      }
    );

    return Object.values(grouped).sort((a, b) =>
      a.valuationDateStr.localeCompare(b.valuationDateStr)
    );
  }, [grantScopeData]);

  // 2. Initialize state from props
  useEffect(() => {
    if (!grantScopeData?.grantDatesWithData) return;

    const initialAssumptions: {
      [key: string]: {
        volatility: number;
        riskFreeRate: number;
        stockPrice: number;
        dividendYield: number;
      };
    } = {};

    groups.forEach((group) => {
      // Take assumptions from the first grant in the group
      // Assuming all grants in the same valuation group share assumptions initially
      // or default to 0 if not set.
      const firstGrantDate = group.grantDates[0];
      const data = grantScopeData.grantDatesWithData[
        firstGrantDate
      ] as BlackScholes;

      initialAssumptions[group.valuationDateStr] = {
        volatility: data.volatility || 0,
        riskFreeRate: data.riskFreeRate || 0,
        stockPrice: data.stockPrice || 0,
        dividendYield: data.dividendYield || 0,
      };
    });

    setGroupAssumptions(initialAssumptions);
  }, [groups, grantScopeData]);

  const handleAssumptionChange = (
    valDateStr: string,
    field: keyof typeof groupAssumptions[string],
    value: string
  ) => {
    const numValue = parseFloat(value) || 0;
    setGroupAssumptions((prev) => ({
      ...prev,
      [valDateStr]: {
        ...prev[valDateStr],
        [field]: numValue,
      },
    }));
  };

  const isGroupComplete = (valDateStr: string) => {
    const assumptions = groupAssumptions[valDateStr];
    return (
      assumptions &&
      assumptions.volatility > 0 &&
      assumptions.riskFreeRate > 0 &&
      assumptions.stockPrice > 0
    );
  };

  const handleApplySameAsAbove = (currentIndex: number) => {
    if (currentIndex === 0) return;
    const currentGroup = groups[currentIndex];
    const previousGroup = groups[currentIndex - 1];

    const prevAssumptions = groupAssumptions[previousGroup.valuationDateStr];
    if (prevAssumptions) {
      setGroupAssumptions((prev) => ({
        ...prev,
        [currentGroup.valuationDateStr]: { ...prevAssumptions },
      }));
    }
  };

  const toggleAccordion = (id: string) => {
    setCollapsedAccordions((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const handleSubmit = () => {
    if (!grantScopeData) return;

    // Validation: Check if all groups have valid assumptions
    const missingDataGroups = groups.filter((group) => {
      const assumptions = groupAssumptions[group.valuationDateStr];
      return (
        !assumptions ||
        assumptions.volatility === undefined ||
        assumptions.riskFreeRate === undefined ||
        assumptions.stockPrice === undefined ||
        assumptions.dividendYield === undefined ||
        assumptions.volatility <= 0 ||
        assumptions.riskFreeRate <= 0 ||
        assumptions.stockPrice <= 0
      );
    });

    if (missingDataGroups.length > 0) {
      toast(
        `Please enter assumptions for the following valuation dates: ${missingDataGroups
          .map((g) => formatDisplayDate(g.valuationDateStr))
          .join(", ")}`,
        { type: "error", autoClose: 5000 }
      );
      return;
    }

    const updatedGrantDatesWithData = { ...grantScopeData.grantDatesWithData };

    groups.forEach((group) => {
      const assumptions = groupAssumptions[group.valuationDateStr];
      group.grantDates.forEach((grantDateKey) => {
        const existingData = updatedGrantDatesWithData[
          grantDateKey
        ] as BlackScholes;

        updatedGrantDatesWithData[grantDateKey] = {
          ...existingData,
          ...assumptions,
        };
      });
    });

    const updatedData = {
      ...grantScopeData,
      grantDatesWithData: updatedGrantDatesWithData,
    };

    if (setGrantScopeData) {
      setGrantScopeData(updatedData);
    }
    onNext(updatedData);
  };

  // Filter groups based on search term
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groups;
    const lowerQuery = searchQuery.toLowerCase();

    return groups.filter((group) => {
      const dateMatch = formatDisplayDate(group.valuationDateStr)
        .toLowerCase()
        .includes(lowerQuery);
      const grantsMatch = group.grantDates.some((grantDate) =>
        formatDisplayDate(grantDate).toLowerCase().includes(lowerQuery)
      );

      return dateMatch || grantsMatch;
    });
  }, [groups, searchQuery]);

  return (
    <Box className="w-full">
      <VStack className="">
        <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
          <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
            Other Valuation Assumptions
          </HStack>
          <p className="text-gray-401 font-medium text-sm3">
            Enter Volatility, Risk-Free Rate, Stock Price, and Dividend Yield
            for each Valuation Date.
          </p>
        </VStack>

        <VStack className="gap-6 w-full p-6">
          <Box className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100">
            <Icon
              icon="mdi:information"
              className="text-blue-600 mt-0.5 shrink-0"
              width={20}
            />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Assumption Details</p>
              <p>
                These assumptions are applied to all grants within the same
                valuation group.
              </p>
            </div>
          </Box>

          {uploadErrors.length > 0 && (
            <div className="p-2 mt-2 overflow-auto border border-red-600 bg-[#c728280d] rounded max-h-48">
              <HStack className="justify-between items-start">
                <HStack className="gap-2">
                  <Icon
                    icon={"material-symbols:error"}
                    color="#c72828"
                    height={20}
                  />
                  <p className="font-medium text-red-600">
                    {uploadErrors.length} Errors Found
                  </p>
                </HStack>
                <button
                  onClick={() => setUploadErrors([])}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Clear errors"
                >
                  <Icon icon="mdi:close" height={20} />
                </button>
              </HStack>
              <ul className="text-red-600 list-disc pl-5 mt-1">
                {uploadErrors.map((err, index) => (
                  <li className="text-xs" key={index}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <HStack className="gap-6 w-full items-end justify-between">
            <Box className="flex-1 max-w-sm">
              <SearchInput
                placeholder="Search Valuation Date"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            <HStack className="gap-6 items-end">
              <Box className="flex gap-3 pb-0.5">
                <ButtonPrimary1
                  onClick={handleDownloadTemplate}
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <Icon icon="mdi:download" height={18} />
                  Download
                </ButtonPrimary1>
                <ButtonPrimary1
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <Icon icon="mdi:upload" height={18} />
                  {isUploading ? "Uploading..." : "Upload"}
                </ButtonPrimary1>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".xlsx"
                  onChange={handleFileChange}
                />
              </Box>
            </HStack>
          </HStack>

          <VStack className="gap-4">
            {filteredGroups.map((group, index) => {
              const isComplete = isGroupComplete(group.valuationDateStr);
              return (
                <CollapsibleComponent
                  key={group.valuationDateStr}
                  label={
                    <Box>
                      <HStack className="gap-4 items-center">
                        <span className="font-medium text-sm text-black-501">{`Valuation Date: ${formatDisplayDate(
                          group.valuationDateStr
                        )}`}</span>
                        {isComplete ? (
                          <div className="flex items-center gap-1 text-green-600 text-xs font-normal">
                            <Icon icon="mdi:check-circle" />
                            <span>Completed</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-amber-600 text-xs font-normal">
                            <Icon icon="mdi:alert-circle" />
                            <span>Pending</span>
                          </div>
                        )}
                      </HStack>
                      <HStack className="gap-0 text-xs text-gray-401 mt-1 divide-x divide-gray-300 flex-wrap">
                        <span className="pr-2">
                          {group.grantDates.length} Grant Dates
                        </span>
                        {groupAssumptions[group.valuationDateStr] &&
                          Object.entries(
                            groupAssumptions[group.valuationDateStr]
                          )
                            .filter(([_, v]) => v !== undefined && v !== null)
                            .map(([key, value]) => (
                              <span key={key} className="px-2">
                                {startCase(key)}: {value}
                              </span>
                            ))}
                      </HStack>
                    </Box>
                  }
                  labelDetail={``}
                  isOpen={collapsedAccordions[group.valuationDateStr] ?? true}
                  onClick={() => toggleAccordion(group.valuationDateStr)}
                >
                  <VStack className="gap-4 py-4">
                    <HStack className="justify-between items-start">
                      <Box className="bg-gray-50 p-3 rounded text-sm text-gray-600 flex-1 mr-4">
                        <span className="font-medium">Grants included: </span>
                        {group.grantDates
                          .map((d) => formatDisplayDate(d))
                          .join(", ")}
                      </Box>

                      {index > 0 && (
                        <ButtonPrimary1
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplySameAsAbove(index);
                          }}
                          className="whitespace-nowrap text-xs py-2 h-auto"
                        >
                          Apply same as above
                        </ButtonPrimary1>
                      )}
                    </HStack>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Box>
                        <Label>Volatility (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={
                            groupAssumptions[group.valuationDateStr]
                              ?.volatility || ""
                          }
                          onChange={(e) =>
                            handleAssumptionChange(
                              group.valuationDateStr,
                              "volatility",
                              e.target.value
                            )
                          }
                          placeholder="e.g. 45.5"
                        />
                      </Box>
                      <Box>
                        <Label>Risk-Free Rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={
                            groupAssumptions[group.valuationDateStr]
                              ?.riskFreeRate || ""
                          }
                          onChange={(e) =>
                            handleAssumptionChange(
                              group.valuationDateStr,
                              "riskFreeRate",
                              e.target.value
                            )
                          }
                          placeholder="e.g. 5.2"
                        />
                      </Box>
                      <Box>
                        <Label>Stock Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={
                            groupAssumptions[group.valuationDateStr]
                              ?.stockPrice || ""
                          }
                          onChange={(e) =>
                            handleAssumptionChange(
                              group.valuationDateStr,
                              "stockPrice",
                              e.target.value
                            )
                          }
                          placeholder="e.g. 100.00"
                        />
                      </Box>
                      <Box>
                        <Label>Dividend Yield (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={
                            groupAssumptions[group.valuationDateStr]
                              ?.dividendYield || ""
                          }
                          onChange={(e) =>
                            handleAssumptionChange(
                              group.valuationDateStr,
                              "dividendYield",
                              e.target.value
                            )
                          }
                          placeholder="e.g. 0"
                        />
                      </Box>
                    </div>
                  </VStack>
                </CollapsibleComponent>
              );
            })}
          </VStack>

          <HStack className="justify-between pt-4 border-t border-gray-200 mt-4">
            <ButtonPrimary1 onClick={onBack}>Back</ButtonPrimary1>
            <ButtonPrimary onClick={handleSubmit}>Next: Review</ButtonPrimary>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};
